/**
 * RCADrawer - Root Cause Analysis slide-in drawer
 *
 * Shows detailed RCA results for a problem cluster:
 * - Root cause summary with confidence and category
 * - Causality chain with step-by-step status
 * - Contributing factors
 * - Actions: Reproduce, Fix, Open in Claude Code
 */
import React, { useState } from 'react';
import { X, CheckCircle, AlertCircle, XCircle, Clock, ChevronDown, ChevronUp, Copy, Check, Zap, FlaskConical, Wrench } from 'lucide-react';
import { components, typography, palette, spacing } from '../utils/designTokens';
import { getRCAResult, getReproductionConfig, getFixConfig, getClaudePrompt } from '../services/demoDataService';

// Status icons for causality chain
const statusIcons = {
  success: { icon: CheckCircle, color: palette.status.success },
  warning: { icon: Clock, color: palette.status.warning },
  error: { icon: XCircle, color: palette.status.danger },
  timeout: { icon: Clock, color: palette.status.warning }
};

// Category badge colors
const categoryColors = {
  caching: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/40' },
  prompt_engineering: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/40' },
  tool_routing: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/40' },
  infrastructure: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/40' }
};

// Causality Chain Step
const CausalityStep = ({ step, isLast }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const statusConfig = statusIcons[step.status] || statusIcons.error;
  const StatusIcon = statusConfig.icon;

  const hasDetails = step.details || step.error_message;

  return (
    <div className="flex gap-3">
      {/* Timeline line and icon */}
      <div className="flex flex-col items-center">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${statusConfig.color}20` }}
        >
          <StatusIcon size={16} style={{ color: statusConfig.color }} />
        </div>
        {!isLast && (
          <div
            className="w-0.5 flex-1 min-h-[24px]"
            style={{ backgroundColor: components.card.borderColor }}
          />
        )}
      </div>

      {/* Step content */}
      <div className="flex-1 pb-4">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => hasDetails && setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-surface-subtle)', color: palette.text.muted }}>
              Step {step.step}
            </span>
            <span className="font-medium" style={{ color: palette.text.primary }}>
              {step.component}
            </span>
          </div>
          {hasDetails && (
            <button className="p-1" style={{ color: palette.text.muted }}>
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}
        </div>

        {/* Error message (always shown if present) */}
        {step.error_message && (
          <div
            className="mt-2 text-sm p-2 rounded"
            style={{
              backgroundColor: 'var(--color-severity-critical-bg)',
              color: palette.status.danger
            }}
          >
            {step.error_message}
          </div>
        )}

        {/* Expandable details */}
        {isExpanded && step.details && (
          <div className="mt-2 text-sm space-y-1" style={{ color: palette.text.supporting }}>
            {Object.entries(step.details).map(([key, value]) => (
              <div key={key} className="flex gap-2">
                <span style={{ color: palette.text.muted }}>{key.replace(/_/g, ' ')}:</span>
                <span className="font-mono">{typeof value === 'boolean' ? (value ? 'yes' : 'no') : String(value)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Copyable text component
const CopyableText = ({ text, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      {label && <div className="text-xs mb-1" style={{ color: palette.text.muted }}>{label}</div>}
      <div className="flex items-start gap-2 p-3 rounded" style={{ backgroundColor: 'var(--color-surface-overlay-hover)' }}>
        <pre
          className="flex-1 text-xs font-mono whitespace-pre-wrap overflow-x-auto"
          style={{ color: palette.text.primary }}
        >
          {text}
        </pre>
        <button
          onClick={handleCopy}
          className="flex-shrink-0 p-1.5 rounded transition-colors hover:bg-background-hover"
          style={{ color: palette.text.muted }}
          title="Copy to clipboard"
        >
          {copied ? <Check size={14} style={{ color: palette.status.success }} /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
};

// Main RCADrawer Component
const RCADrawer = ({
  isOpen,
  onClose,
  cluster,
  onReproduce,
  onFix
}) => {
  if (!isOpen || !cluster) return null;

  // Get RCA data for this cluster
  const rca = getRCAResult(cluster.cluster_id);
  const reproConfig = getReproductionConfig(cluster.cluster_id);
  const fixConfig = getFixConfig(cluster.cluster_id);

  if (!rca) {
    return (
      <div className="fixed inset-0 z-50 flex">
        <div className="flex-1 bg-black/40" onClick={onClose} />
        <div
          className="w-[600px] h-full overflow-hidden flex flex-col"
          style={{ backgroundColor: 'var(--color-input-bg)', borderLeft: `1px solid ${components.card.borderColor}` }}
        >
          <div className="p-6">
            <p style={{ color: palette.text.muted }}>No RCA data available for this cluster.</p>
          </div>
        </div>
      </div>
    );
  }

  const categoryStyle = categoryColors[rca.root_cause.category] || categoryColors.infrastructure;
  const confidencePercent = Math.round(rca.root_cause.confidence * 100);

  // Generate Claude prompt for this cluster
  const claudePrompt = getClaudePrompt(cluster, rca, fixConfig);

  const handleCopyPrompt = async () => {
    await navigator.clipboard.writeText(claudePrompt);
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/40" onClick={onClose} />

      {/* Drawer */}
      <div
        className="w-[600px] h-full overflow-hidden flex flex-col animate-slide-in-right"
        style={{
          backgroundColor: 'var(--color-input-bg)',
          borderLeft: `1px solid ${components.card.borderColor}`
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: `1px solid ${components.card.borderColor}` }}
        >
          <div>
            <h2 className="text-lg font-semibold" style={{ color: palette.text.primary }}>
              Root Cause Analysis
            </h2>
            <p className="text-sm" style={{ color: palette.text.muted }}>
              {cluster.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded transition-colors hover:bg-background-hover"
            style={{ color: palette.text.muted }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Root Cause Summary */}
          <div>
            <h3
              className="text-xs uppercase mb-3"
              style={{ color: palette.text.muted, letterSpacing: '0.05em' }}
            >
              Root Cause
            </h3>
            <div
              className={`${components.card.rounded} p-4`}
              style={{ backgroundColor: 'var(--color-surface-overlay-hover)', border: `1px solid ${components.card.borderColor}` }}
            >
              <p className="mb-3" style={{ color: palette.text.primary }}>
                {rca.root_cause.summary}
              </p>
              <div className="flex items-center gap-3">
                <span
                  className={`px-2 py-1 rounded text-xs ${categoryStyle.bg} ${categoryStyle.text} border ${categoryStyle.border}`}
                >
                  {rca.root_cause.category.replace(/_/g, ' ')}
                </span>
                <span className="text-sm" style={{ color: palette.text.supporting }}>
                  Confidence: <span style={{ color: palette.text.primary, fontWeight: 600 }}>{confidencePercent}%</span>
                </span>
              </div>
            </div>
          </div>

          {/* Causality Chain */}
          <div>
            <h3
              className="text-xs uppercase mb-3"
              style={{ color: palette.text.muted, letterSpacing: '0.05em' }}
            >
              Causality Chain
            </h3>
            <div className="space-y-0">
              {rca.causality_chain.map((step, idx) => (
                <CausalityStep
                  key={step.step}
                  step={step}
                  isLast={idx === rca.causality_chain.length - 1}
                />
              ))}
            </div>
          </div>

          {/* Contributing Factors */}
          <div>
            <h3
              className="text-xs uppercase mb-3"
              style={{ color: palette.text.muted, letterSpacing: '0.05em' }}
            >
              Contributing Factors
            </h3>
            <ul className="space-y-2">
              {rca.contributing_factors.map((factor, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: palette.accents.warm.base }} />
                  <span className="text-sm" style={{ color: palette.text.supporting }}>{factor}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Recommended Fix */}
          {fixConfig && (
            <div>
              <h3
                className="text-xs uppercase mb-3"
                style={{ color: palette.text.muted, letterSpacing: '0.05em' }}
              >
                Recommended Fix
              </h3>
              <div
                className={`${components.card.rounded} p-4 space-y-3`}
                style={{ backgroundColor: 'var(--color-surface-overlay-hover)', border: `1px solid ${components.card.borderColor}` }}
              >
                <div>
                  <span className="text-xs" style={{ color: palette.text.muted }}>File: </span>
                  <code className="text-sm font-mono" style={{ color: palette.accents.primary.base }}>
                    {fixConfig.code_changes.file}
                  </code>
                </div>
                <div>
                  <span className="text-xs" style={{ color: palette.text.muted }}>Function: </span>
                  <code className="text-sm font-mono" style={{ color: palette.accents.warm.base }}>
                    {fixConfig.code_changes.function}
                  </code>
                </div>
                <ul className="space-y-1">
                  {fixConfig.code_changes.recommendations.map((rec, idx) => (
                    <li key={idx} className="text-sm flex items-start gap-2" style={{ color: palette.text.supporting }}>
                      <span style={{ color: palette.status.success }}>→</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Claude Code Prompt */}
          <div>
            <h3
              className="text-xs uppercase mb-3"
              style={{ color: palette.text.muted, letterSpacing: '0.05em' }}
            >
              Claude Code Prompt
            </h3>
            <CopyableText text={claudePrompt} />
          </div>
        </div>

        {/* Footer Actions */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderTop: `1px solid ${components.card.borderColor}` }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded transition-colors hover:bg-background-hover"
            style={{ color: palette.text.supporting }}
          >
            Close
          </button>
          <div className="flex items-center gap-3">
            {onReproduce && (
              <button
                onClick={() => onReproduce(cluster, reproConfig)}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded transition-colors"
                style={{
                  backgroundColor: 'var(--color-surface-mid)',
                  color: palette.text.primary,
                  border: `1px solid ${components.card.borderColor}`
                }}
              >
                <FlaskConical size={16} />
                Reproduce
              </button>
            )}
            {onFix && (
              <button
                onClick={() => onFix(cluster, fixConfig)}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded transition-colors"
                style={{
                  backgroundColor: palette.accents.primary.base,
                  color: 'var(--color-button-text-on-accent)'
                }}
              >
                <Wrench size={16} />
                Test Fix
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Slide-in animation */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default RCADrawer;
